/**
 * Whether an ability is done to its own user, and therefore needs no target.
 *
 * Long Rest, Breakthrough, Flow Accumulation and every skill whose reach is
 * zero resolve against the caster and can land nowhere else. Asking the player
 * to pick a body for one of them — or a cell on the battle map — asks for a
 * choice that does not exist, and on the tactical screen there was frequently
 * no legal cell to give: the ability's own range refused every one of them.
 *
 * The server is the authority and sends `self_target` on skills and special
 * actions alike. The range fallback is for a client holding an older payload;
 * it says the same thing the server does, from the same two numbers.
 *
 * Pure: no Vue, no API client.
 */

function abilityRules(ability) {
  if (!ability) return null;
  // A world item carries its ability on the item template's skill.
  return ability.item?.skill || ability.skill || ability;
}

export function isSelfTargetAbility(ability) {
  const rules = abilityRules(ability);
  if (!rules) return false;
  if (typeof rules.self_target === 'boolean') return rules.self_target;
  return rules.max_range === 0;
}

/**
 * One aim per charge, every one of them the caster's own cell.
 *
 * The count is passed in rather than read off the ability: the caller already
 * resolved it, and a second reading that disagreed would build an aim list the
 * cast then refuses for being the wrong length.
 */
export function selfTargetAims(casterCell, charges = 1) {
  if (!casterCell) return [];
  return Array.from({length: Math.max(1, charges)}, () => ({
    x: casterCell.x,
    y: casterCell.y,
    rotation: 0,
  }));
}
